"use client";

import { useState } from "react";
import {
  Checkbox,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { useToolUi } from "@/components/tools/tool-ui";
import { ChartTable } from "@/components/tools/worksheets-math/chart-table";
import { WORKSHEET_COMMON_STRINGS } from "@/components/tools/worksheets-math/common-strings";
import {
  ActionBar,
  defaultSettings,
  useCommonSettings,
  useWorksheetSeed,
  WorksheetSheets,
} from "@/components/tools/worksheets-math/worksheet-kit";
import { t } from "@/lib/i18n/interpolate";
import {
  blankCells,
  chartFactors,
  MULTIPLICATION_FONT_POINTS,
  MULTIPLICATION_SIZES,
  type MultiplicationSize,
} from "@/lib/tool-kit/worksheets/charts";
import { createRng } from "@/lib/tool-kit/worksheets/random";

/** English strings. A translated content file overrides these through `ui`. */
export const MULTIPLICATION_CHART_STRINGS = {
  ...WORKSHEET_COMMON_STRINGS,
  defaultTitle: "Multiplication Chart",
  sizeLabel: "Chart size",
  sizeOption: "{size} × {size}",
  zeroLabel: "Include a row and column for 0",
  fillLabel: "Products shown",
  fills: {
    complete: "All products — a complete chart",
    q25: "Hide a quarter — fill in the gaps",
    q50: "Hide half — fill in the gaps",
    q75: "Hide three quarters — fill in the gaps",
    blank: "Hide every product — a blank chart",
  },
  highlightLabel: "Highlight a number's row and column",
  highlightNone: "No highlight",
  squaresLabel: "Shade the square numbers along the diagonal",
  corner: "×",
  chartLabel: "Multiplication chart, {size} by {size}",
} as const;

type Fill = keyof typeof MULTIPLICATION_CHART_STRINGS.fills;

const FILL_FRACTION: Record<Fill, number> = {
  complete: 0,
  q25: 0.25,
  q50: 0.5,
  q75: 0.75,
  blank: 1,
};

export default function MultiplicationChartWidget() {
  const strings = useToolUi(MULTIPLICATION_CHART_STRINGS);
  const [settings, update] = useCommonSettings(
    defaultSettings({ header: false, answerKey: true })
  );
  const seed = useWorksheetSeed();
  const [size, setSize] = useState<MultiplicationSize>(12);
  const [zero, setZero] = useState(false);
  const [fill, setFill] = useState<Fill>("complete");
  const [focus, setFocus] = useState(0);
  const [squares, setSquares] = useState(false);

  const factors = chartFactors(size, zero);
  const fraction = FILL_FRACTION[fill];
  const hasGaps = fraction > 0;
  const usesSeed = fraction > 0 && fraction < 1;
  const blanks = hasGaps
    ? blankCells(factors, fraction, createRng(seed.seed))
    : null;
  const sizeText = String(size);
  const label = t(strings.chartLabel, { size: sizeText });
  const chart = (shown: ReadonlySet<string> | null) => (
    <ChartTable
      blanks={shown}
      corner={strings.corner}
      factors={factors}
      focus={focus === 0 ? null : focus}
      fontPoints={MULTIPLICATION_FONT_POINTS[size]}
      label={label}
      squares={squares}
    />
  );

  return (
    <div className="space-y-6 print:space-y-0">
      <div className="space-y-5 print:hidden">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SelectField
            id="mc-size"
            label={strings.sizeLabel}
            onChange={(value) => {
              setSize(Number(value) as MultiplicationSize);
              setFocus(0);
            }}
            options={MULTIPLICATION_SIZES.map((value) => ({
              value: String(value),
              label: t(strings.sizeOption, { size: String(value) }),
            }))}
            value={sizeText}
          />
          <SelectField
            id="mc-fill"
            label={strings.fillLabel}
            onChange={(value: Fill) => setFill(value)}
            options={(Object.keys(FILL_FRACTION) as Fill[]).map((value) => ({
              value,
              label: strings.fills[value],
            }))}
            value={fill}
          />
          <SelectField
            id="mc-focus"
            label={strings.highlightLabel}
            onChange={(value) => setFocus(Number(value))}
            options={[
              { value: "0", label: strings.highlightNone },
              ...factors
                .filter((value) => value > 0)
                .map((value) => ({
                  value: String(value),
                  label: String(value),
                })),
            ]}
            value={String(focus)}
          />
          <SelectField
            id="mc-paper"
            label={strings.paperLabel}
            onChange={(paper) => update({ paper })}
            options={(["letter", "a4"] as const).map((value) => ({
              value,
              label: strings.papers[value],
            }))}
            value={settings.paper}
          />
        </div>
        <TextField
          hint={strings.titleHint}
          id="mc-title"
          label={strings.titleLabel}
          onChange={(title) => update({ title })}
          value={settings.title}
        />
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={zero}
            label={strings.zeroLabel}
            onChange={setZero}
          />
          <Checkbox
            checked={squares}
            label={strings.squaresLabel}
            onChange={setSquares}
          />
          <Checkbox
            checked={settings.header}
            label={strings.showHeader}
            onChange={(header) => update({ header })}
          />
          {hasGaps ? (
            <Checkbox
              checked={settings.answerKey}
              label={strings.answerKey}
              onChange={(answerKey) => update({ answerKey })}
            />
          ) : null}
        </div>
        <ActionBar
          idPrefix="mc"
          seed={seed}
          showSeed={usesSeed}
          strings={strings}
        />
      </div>
      <WorksheetSheets
        answers={chart(null)}
        problems={chart(blanks)}
        seed={seed.seed}
        settings={{
          ...settings,
          answerKey: settings.answerKey && hasGaps,
          fontSize: "medium",
        }}
        showSeed={usesSeed}
        strings={strings}
        title={settings.title.trim() || strings.defaultTitle}
      />
    </div>
  );
}
